<script lang="ts">
  import { m } from '$lib/paraglide/messages';
  import { translation } from '$lib/locale/locale';
  import type { Subscriptions } from '$lib/models/init';
  import type { User } from '$lib/models/user';
  import { AssetType, downloadAsset, searchLink } from '$lib/utils/asset';
  import { srcset } from '$lib/utils/srcset';
  import Button from '$lib/ui/shared/buttons/button.svelte';
  import downloadIcon from '$lib/assets/shared/download.svg';
  import loadingIcon from '$lib/assets/shared/loading.svg';
  import similarIcon from '$lib/assets/search/similar.svg';
  import wishlistIcon from '$lib/assets/shared/save.svg';
  import chevronDownIcon from '$lib/assets/shared/chevron-down.svg';
  import versionsZrpImage from '$lib/assets/asset-detail/versions-zrp.svg';
  import type { Track } from '$lib/models/track';
  import { openModal } from '$lib/ui/svelte-modals';
  import LitePlayer from '$lib/ui/player/lite-player.svelte';
  import AssetSpecs from '$lib/ui/track-detail/asset-specs.svelte';
  import AssetList from '$lib/ui/shared/asset-list.svelte';
  import { Tab } from '$lib/models/tab';
  import Tabs from '$lib/ui/shared/tabs.svelte';
  import VersionPlayer from '$lib/ui/player/version-player.svelte';
  import { CartItemFrom } from '$lib/models/cart-item';
  import { canDownload } from '$lib/utils/download';
  import { purchaseSfxModal } from '$lib/state/purchase-sfx-modal.svelte';
  import { startTrialFlow } from '$lib/ui/free-trial/trial-flow';
  import { purchaseModal } from '$lib/state/purchase-modal.svelte';
  import { authModal } from '$lib/state/auth.svelte';

  const { langHref, p } = translation();

  export let asset: Track;
  export let subscriptions: Subscriptions;
  export let user: User | null | void;

  $: asset?.id;

  let showLicensingModal = false;
  let downloading = false;
  let timer: any;
  let loading = false;

  let tabs = [new Tab('loops', m['track-details.loops']())];
  if (asset.type === AssetType.Track) {
    tabs.push(new Tab('stems', m['track-details.stems'](), m['track-details.coming-soon'](), true));
  }
  let selectedTab: Tab;

  let leftTabs = [new Tab('details', m['common.details']())];

  function handleTabChange(event: any) {
    let tab = event.detail.tab;
    if (tab) {
      selectedTab = tab;
    }
  }

  function download() {
    if (canDownload(asset, user)) {
      if (loading || downloading) return;
      downloading = true;
      loading = true;
      clearTimeout(timer);
      timer = setTimeout(() => {
        loading = false;
      }, 10000); // 10 seconds

      downloadAsset(asset).then((_) => {
        downloading = false;
      });
    }
  }

  function openWishlistModal() {
    if (user) {
      openModal(() => import('$lib/ui/modals/add-to-wishlist.svelte'), { track: asset });
    } else {
      authModal.show(false);
    }
  }

  let innerWidth: number;
  let accordionContainer: HTMLElement;
  let detailsContainer: HTMLElement;
  let versionsContainer: HTMLElement;
  let activeElementContainer: HTMLElement | null;
  let activeButton: HTMLElement | null;

  function openAccordion(button: EventTarget | null, element: HTMLElement | null) {
    // Get or create the container where the active element will be placed.
    let container = document.getElementById('accordion-active-content');
    if (!container) {
      container = document.createElement('div');
      container.id = 'accordion-active-content';
    }
    // If there is an active element, remove it from the container and place it back in its original place.
    if (activeElementContainer) {
      while (container.firstChild) {
        activeElementContainer.appendChild(container.firstChild);
      }
      activeElementContainer = null;
    }
    // If there is an active button, remove the active class.
    let htmlButton = button as HTMLElement;
    if (htmlButton == activeButton) {
      activeButton = null;
      container.remove();
      return;
    } else {
      activeButton = null;
    }
    if (element) {
      activeElementContainer = element;
      while (element.firstChild) {
        container.appendChild(element.firstChild);
      }
      if (htmlButton) {
        activeButton = htmlButton;
        htmlButton.after(container);
        accordionContainer.scrollIntoView({
          behavior: 'smooth',
        });
      } else {
        container.remove();
      }
    }
  }

  $: similarToLink = searchLink(langHref, asset.type, new URLSearchParams({ similarTo: asset.id.toString() }));
  $: cartFrom =
    asset.type === AssetType.SFX
      ? CartItemFrom.SfxDetail
      : asset.type === AssetType.Intro
        ? CartItemFrom.IntroDetail
        : CartItemFrom.TrackDetail;

  // If we are on desktop, close the accordion
  // and return all items to their original position.
  $: if (innerWidth > 1190) {
    openAccordion(null, null);
  }
</script>

<svelte:window bind:innerWidth />

<div class="grey-background">
  <div class="container-ld">
    <div class="hero">
      <div class="left">
        <div class="d-flex flex-row">
          {#if asset.type === AssetType.Track}
            <img class="track-image-desktop" alt={asset.title} srcset={srcset(asset.srcset)} sizes="176px" fetchpriority="high" />
          {/if}
          <div class="player-container">
            <div class="d-flex flex-row">
              {#if asset.type === AssetType.Track}
                <img class="track-image-mobile" alt={asset.title} srcset={srcset(asset.srcset)} sizes="100px" fetchpriority="high" />
              {/if}

              <div class="d-flex flex-column">
                <h1 class="title">{asset.title}</h1>
                <div class="actions-row">
                  <!-- <div class="d-flex flex-row"> -->
                  <a rel="nofollow" class="grey-cta" href={similarToLink}>
                    <img class="img_text-invert-secondary icon" src={similarIcon} alt="" aria-hidden="true" />
                    {m['actions.find-similar']()}
                  </a>
                  <!-- </div> -->

                  <button class="grey-cta" on:click={openWishlistModal}>
                    <img class="img_text-invert-secondary icon" src={wishlistIcon} alt="" aria-hidden="true" />
                    {m['wishlist.title']()}
                  </button>
                </div>
              </div>
            </div>

            <div class="mini-player">
              <LitePlayer {asset} />
            </div>
          </div>
        </div>
        <div class="mobile-accordion-item-origin" bind:this={detailsContainer}>
          <div class="details-tabs">
            <Tabs
              tabs={leftTabs}
              selectedTab={leftTabs[0]}
              position="left"
              borderBottom="1px solid var(--border-700)"
              inactiveColor="var(--text-invert-secondary)"
              inactiveBorderBottom="none"
              activeColor="var(--text-invert)"
              activeBorderBottom="1px solid var(--magic-potion)"
              tabMarginBottom="-1px"
              tabPadding="12px 24px 12px 4px"
            />
          </div>
          <AssetSpecs {asset} />
        </div>
      </div>

      <div class="right-column">
        {#if !user?.subscription.hasTracksAccess && asset.type === AssetType.Track}
          <div class="download-title">{m['track-details.download-this-music']()}</div>
        {:else if !user?.subscription.hasSfxAccess && asset.type === AssetType.SFX}
          <div class="download-title">{m['sfx-details.download-this-sfx']()}</div>
        {:else if !user?.subscription.hasIntrosAccess && asset.type === AssetType.Intro}
          <div class="download-title">{m['intro-details.download-this-intro']()}</div>
        {/if}

        {#if !user?.subscription.hasTracksAccess}
          <div class="download-subtitle">
            {@html m['track-details.unlimited-music-pricing']({
              oldPrice: p(subscriptions?.pro?.annually?.originalMonthlyPrice),
              price: p(subscriptions?.pro?.annually?.monthlyPrice),
            })}
          </div>

          <div class="buttons-row">
            <Button
              text={m['actions.download']()}
              width="auto"
              paddingTop="12px"
              paddingBottom="12px"
              paddingRight="16px"
              paddingLeft="16px"
              paddingBottomMobile="10px"
              paddingRightMobile="16px"
              paddingLeftMobile="16px"
              paddingTopMobile="10px"
              fontSizeMobile="var(--xs-font-size)"
              flex={1}
              onClick={() =>
                asset.type === AssetType.SFX
                  ? purchaseSfxModal.show(asset)
                  : asset.type === AssetType.Intro
                    ? startTrialFlow(cartFrom)
                    : purchaseModal.show(asset)}
            />
          </div>
        {:else}
          <div>
            <div class="current-plan">
              {#if user?.subscription.isTrial}
                <div class="subscribed crayola asset-detail-trialling">{@html m['track-details.trialling']()}</div>
              {:else}
                <div class="subscribed crayola">{m['track-details.subscribed']()}</div>
              {/if}
              <div class="plan-name">{user?.subscription.name}</div>
              <div class="track-info crayola">{asset.title}</div>
              {#if asset.filename || asset.versions}
                <div class="track-info">
                  {#if asset.filename}
                    {asset.filename}
                  {/if}
                  {#if asset.filename && asset.versions}
                    -
                  {/if}
                  {#if asset.versions}
                    {m['track-details.versions']()}: {asset.versions}
                  {/if}
                </div>
              {/if}
            </div>
            <Button
              text={m['actions.download']()}
              onClick={download}
              rightImage={loading ? loadingIcon : downloadIcon}
              {loading}
              paddingTop="12px"
              paddingBottom="12px"
              paddingRight="16px"
              paddingLeft="16px"
              paddingBottomMobile="10px"
              paddingRightMobile="16px"
              paddingLeftMobile="16px"
              paddingTopMobile="10px"
              fontSizeMobile="var(--xs-font-size)"
              imageWidthMobile="16px"
              imageHeightMobile="16px"
            />
          </div>
        {/if}
        <div class="mobile-accordion-item-origin" bind:this={versionsContainer}>
          <div class="versions">
            <div class="versions-tabs">
              <Tabs
                {selectedTab}
                {tabs}
                on:tabChanged={handleTabChange}
                position="left"
                borderBottom="1px solid var(--border-700)"
                inactiveColor="var(--text-invert-secondary)"
                inactiveBorderBottom="none"
                activeColor="var(--text-invert)"
                activeBorderBottom="1px solid var(--magic-potion)"
                tabMarginBottom="-1px"
                tabPadding="12px 24px 12px 4px"
              />
            </div>

            {#if !asset.mp3Versions?.length}
              <div class="versions-zrp">
                <img src={versionsZrpImage} alt="" aria-hidden="true" loading="lazy" />
                <div>{m['track-details.no-loops']()}</div>
              </div>
            {:else}
              <ul>
                {#if asset.mp3MainVersion}
                  <li>
                    <VersionPlayer asset={asset.mp3MainVersion} mainVersion={true} mainAsset={asset} {user} />
                  </li>
                {/if}
                {#each asset.mp3Versions as version}
                  <li><VersionPlayer asset={version} mainAsset={asset} {user} /></li>
                {/each}
              </ul>
            {/if}
          </div>
        </div>
        <div class="mobile-accordion" bind:this={accordionContainer}>
          <button
            on:click={(e) => openAccordion(e.target, detailsContainer)}
            class:active={activeElementContainer && activeElementContainer === detailsContainer}
          >
            {m['common.details']()}
            <img src={chevronDownIcon} alt="" aria-hidden="true" loading="lazy" />
          </button>
          <button
            on:click={(e) => openAccordion(e.target, versionsContainer)}
            class:active={activeElementContainer && activeElementContainer === versionsContainer}
          >
            {m['track-details.loops']()}
            <img src={chevronDownIcon} alt="" aria-hidden="true" loading="lazy" />
          </button>
        </div>
      </div>
    </div>
  </div>
</div>

<div class="container-ld">
  {#if asset.type === AssetType.Track}
    <h2 class="similar-tracks">{m['track-details.similar-tracks']()}</h2>
  {:else if asset.type === AssetType.SFX}
    <h2 class="similar-tracks">{m['sound-effect-details.similar']()}</h2>
  {:else if asset.type === AssetType.Intro}
    <h2 class="similar-tracks">{m['intro-details.similar']()}</h2>
  {/if}

  <AssetList tracks={asset.additionalTracks.similar} type={asset.type} {user} showSimilarIcon={true} />
</div>

{#if showLicensingModal}
  {#await import('$lib/ui/modals/purchase-license.svelte') then LazyPurchaseLicense}
    <LazyPurchaseLicense.default {asset} on:close={() => (showLicensingModal = false)} />
  {/await}
{/if}

<style>
  .hero {
    display: flex;
    flex-direction: row;
  }
  .grey-background {
    background-color: var(--chinese-black);
    padding-top: 80px;
    padding-bottom: 62px;
  }
  .left {
    width: 60%;
    margin-right: 48px;
  }
  .right-column {
    width: 40%;
    max-width: 716px;
    justify-content: flex-start;
  }

  .title {
    color: var(--text-invert);
    font-size: var(--xl-font-size);
    font-weight: var(--bold-font-weight);
    margin-bottom: 8px;
    margin-top: 6px;
    line-height: 36px;
  }

  .track-image-desktop {
    width: 100%;
    max-width: 176px;
    max-height: 176px;
    border-radius: 16px;
    margin-bottom: 16px;
    aspect-ratio: 1;
    margin-right: 20px;
  }

  .track-image-mobile {
    display: none;
    max-width: 100px;
    max-height: 100px;
    margin-right: 12px;
    aspect-ratio: 1;
    margin-bottom: 17px;
  }

  .actions-row {
    display: flex;
    flex-direction: row;
    gap: 4px 8px;
  }

  .grey-cta {
    display: flex;
    flex-direction: row;
    align-items: center;
    background-color: var(--chinese-black);
    color: var(--text-invert-secondary);
    border: solid 1px var(--charleston-grey);
    border-radius: 16px;
    padding: 4px 12px;
    text-align: center;
    font-size: var(--base-font-size);
  }

  .player-container {
    width: 100%;
  }
  .mini-player {
    margin-top: 24px;
    width: 100%;
  }
  .download-title {
    color: var(--lotion);
    font-size: var(--xl-font-size);
    font-weight: var(--bold-font-weight);
    margin-bottom: 16px;
  }
  .download-subtitle {
    color: var(--lotion);
    margin-bottom: 16px;
    font-size: var(--base-font-size);
    font-weight: var(--normal-font-weight);
  }
  .crayola {
    color: var(--crayola);
  }

  .buttons-row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
  }

  /* Subscribed */

  .current-plan {
    background-color: var(--charleston-grey);
    color: var(--lotion);
    border-radius: 8px;
    padding: 24px;
    text-align: center;
    margin-bottom: 18px;
  }
  .plan-name {
    margin-bottom: 16px;
    font-size: var(--xl-font-size);
    font-weight: var(--bold-font-weight);
  }

  .subscribed {
    font-size: var(--base-font-size);
    font-weight: var(--normal-font-weight);
  }
  .track-info {
    font-weight: var(--normal-font-weight);
    font-size: var(--md-font-size);
  }

  .asset-detail-trialling {
    padding-left: 30px;
    padding-right: 30px;
    padding-bottom: 10px;
  }
  .similar-tracks {
    font-size: var(--lg-font-size);
    font-weight: var(--bold-font-weight);
    text-align: left;
    margin-top: 60px;
    margin-bottom: 24px;
  }

  .icon {
    width: 20px;
    height: 20px;
    margin-right: 8px;
  }

  :global(.asset-detail-trialling b) {
    border-radius: 4px;
    background: #5a52ff;
    font-weight: 500;
    line-height: 28px;
    color: #f0f1ff;
    padding: 5px 10px;
  }

  :global(.download-subtitle b) {
    font-weight: var(--bold-font-weight);
  }

  .versions {
    margin-top: 50px;
  }

  .versions ul {
    list-style-type: none;
    padding: 0;
  }

  .versions li {
    border-bottom: 1px solid var(--border-900);
  }
  .versions li:last-child {
    border: none;
  }

  .versions-zrp {
    display: flex;
    flex-direction: column;
    align-items: center;
    color: var(--text-invert);
  }

  .versions-zrp img {
    margin-top: 20px;
    margin-bottom: 12px;
    width: 240px;
    height: 132px;
  }

  .versions-zrp div {
    width: 240px;
    text-align: center;
    font-size: var(--xs-font-size);
  }

  .mobile-accordion {
    display: none;
  }

  .details-tabs {
    margin-top: 39px;
  }

  @media (max-width: 900px) {
    .grey-background {
      padding-top: 33px;
      padding-bottom: 24px;
    }

    .hero {
      flex-direction: column;
      align-items: center;
      font-size: var(--normal-xs);
    }

    .left {
      margin-right: 0;
      width: 100%;
      max-width: 700px;
      margin-bottom: 18px;
    }

    .right-column {
      width: 100%;
      max-width: 700px;
    }

    .player-container {
      max-width: none;
    }

    .download-title {
      font-size: var(--md-font-size);
      margin-bottom: 12px;
    }

    .download-subtitle {
      font-size: var(--xs-font-size);
    }

    /* Subscribed users info */
    .plan-name {
      font-size: var(--lg-font-size);
    }
    .subscribed {
      font-size: var(--xs-font-size);
    }
    .track-info {
      font-size: var(--base-font-size);
    }

    /* End subscribed users info */

    .track-image-desktop {
      display: none;
    }
    .track-image-mobile {
      display: block;
      border-radius: 8px;
    }
    .title {
      font-size: var(--base-font-size);
      margin-bottom: 8px;
      margin-top: 6px;
      line-height: 24px;
    }
    .mini-player {
      margin-top: 0;
    }

    .actions-row {
      flex-wrap: wrap;
    }

    .grey-cta {
      font-size: var(--xs-font-size);
      padding: 3px 8px;
    }

    .icon {
      width: 16px;
      height: 16px;
      margin-right: 4px;
    }

    .versions {
      margin-top: 20px;
    }
    .versions-tabs {
      display: none;
    }
    .mobile-accordion {
      display: flex;
      flex-direction: column;
      margin-top: 38px;
      margin-bottom: 20px;
      scroll-margin-top: 100px;
    }

    .mobile-accordion button {
      width: 100%;
      text-align: left;
      border-bottom: 1px solid var(--border-700);
      color: var(--text-invert-secondary);
      font-size: var(--base-font-size);
      padding: 16px 4px;
      display: flex;
      justify-content: space-between;
    }

    .mobile-accordion button.active {
      color: var(--text-invert);
      border-bottom: 1px solid var(--magic-potion);
    }
    .mobile-accordion button img {
      width: 20px;
      height: 20px;
      pointer-events: none;
    }

    .mobile-accordion button.active img {
      transform: rotate(180deg);
    }

    .mobile-accordion-item-origin {
      display: none;
    }
    .details-tabs {
      display: none;
    }
  }
</style>
